import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { skipReasonCopy, useUnbilled } from '@/lib/invoice-run-hooks';

/**
 * Customers with no invoice for a month, and why.
 *
 * The one durable surface a human is meant to act on. Everything else about a
 * generation run — the run row, its items, the progress — is machinery; this is
 * the part with someone's name on it.
 *
 * **Self-healing.** The server drops benign outcomes and anyone since invoiced,
 * so fixing the SOW and pressing Generate again makes a row disappear. There is
 * nothing to dismiss and no state to keep in sync. The only way to say "we know,
 * stop telling us" is to pause invoicing on the customer, which is a real
 * decision recorded in a real place.
 *
 * Rendered only when there is something to say — an empty panel on a healthy
 * month is noise that teaches people to ignore the space.
 */
export function UnbilledCustomersPanel({ month }: { month: string | null }) {
  const { data, isError, error } = useUnbilled(month);

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive/50 p-3" role="alert">
        <p className="text-destructive text-sm">
          {error instanceof Error
            ? error.message
            : "Couldn't check which customers are missing an invoice."}
        </p>
      </div>
    );
  }

  const rows = data?.data ?? [];
  if (rows.length === 0) return null;

  const label = new Date(`${rows[0]?.invoiceMonth}T00:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-3">
      <div className="mb-2.5 flex items-center gap-2">
        <AlertTriangle aria-hidden className="size-4 text-amber-600" />
        <p className="font-medium text-sm">
          {/* Counted in SOWs, because that is what was not invoiced. Saying
              "2 customers" when one customer holds both would send someone
              looking for a second name that does not exist. */}
          {rows.length} {rows.length === 1 ? 'engagement wasn’t' : 'engagements weren’t'} invoiced
          for {label}
        </p>
      </div>

      <ul className="divide-y rounded-md border bg-background">
        {rows.map((row) => {
          // Optional-chained below, matching the run detail page: the API can
          // send a reason a web build deployed before it does not know, and
          // this panel threw on that rather than degrading.
          const copy = skipReasonCopy(row.reason);
          return (
            // Keyed on the document's SUBJECT and its span, the same identity
            // the invoice and the run item use: an opted-out engagement and
            // the customer's gathered document are two rows, and so are two
            // periods of one subject — keying on the customer alone collides.
            <li
              key={`${row.engagementId ?? row.customerId}_${row.periodStart}_${row.periodEnd}`}
              className="flex items-center gap-3 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-sm">
                  {row.customerName}
                  {/* Only when the document names ONE agreement. A gathered
                      invoice covers several, so a SOW name beside the customer
                      would claim the gap belongs to that one agreement when it
                      belongs to all of them. */}
                  {row.sowId === null ? null : (
                    <span className="ml-1.5 font-normal text-muted-foreground">{row.sowName}</span>
                  )}
                </p>
                <p
                  className={`text-xs ${row.systemError ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {copy?.label ?? 'Skipped.'}
                </p>
              </div>
              {/* No call to action for a system error: offering one would be a
                  lie, since nothing the user can do fixes it. */}
              {copy?.fix != null && (
                <Button asChild size="sm" variant="outline">
                  <Link to="/customers/$id" params={{ id: row.customerId }}>
                    Open customer
                  </Link>
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <p className="mt-2 text-muted-foreground text-xs">
        Rows clear themselves once the invoice exists.
      </p>
    </div>
  );
}
